import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmoothMarkdown } from '@/components/chat/SmoothMarkdown';
import { Badge } from '@/components/ui/misc';
import { usd } from '@/lib/format';
import { helpAnswer } from '@/lib/help-answer';
import { type KbAnswerState, sendKbFeedback } from '@/lib/kb';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

const REASON_KEY = {
  no_key: 'help.aiNoKey',
  rate_limited: 'help.aiLimit',
  budget: 'help.aiBudget',
  error: 'help.aiError',
} as const;

/** Articles stay secondary to the answer; additional references are opt-in. */
export function HelpSources({
  sources,
  className,
  related = false,
}: {
  sources: KbAnswerState['sources'];
  className?: string;
  related?: boolean;
}) {
  const { t } = useTranslation();
  const [first, ...rest] = sources;
  if (!first) return null;
  const articleLink = (s: KbAnswerState['sources'][number]) => (
    <Link
      key={s.n}
      to="/help/a/$slug"
      params={{ slug: s.slug }}
      className="text-muted underline decoration-border underline-offset-2 hover:text-fg"
      title={s.heading || s.title}
    >
      {s.title}
    </Link>
  );
  return (
    <div className={cn('space-y-1.5 text-xs leading-relaxed', className)}>
      <p>
        <span className="text-muted">{t(related ? 'help.related' : 'help.readMore')}: </span>
        {articleLink(first)}
      </p>
      {rest.length ? (
        <details>
          <summary className="cursor-pointer text-muted hover:text-fg">
            {t(related ? 'help.moreArticles' : 'help.usedArticles', { count: sources.length })}
          </summary>
          <ul className="mt-2 space-y-1.5 pl-4">
            {sources.map((s) => (
              <li key={s.n}>{articleLink(s)}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/** The streamed summary of the Help Center search ("Ask AI"). */
export function HelpAnswerCard({ state, onRetry }: { state: KbAnswerState; onRetry?: () => void }) {
  const { t } = useTranslation();
  // The platform pays for help answers: its admins see what each one cost.
  const platformAdmin = useMe().data?.platformAdmin === true;
  const [voted, setVoted] = useState<boolean | null>(null);
  if (state.status === 'idle') return null;
  const answer = helpAnswer(state.text, state.sources);
  const unavailable =
    state.status === 'unavailable' || state.status === 'error' || state.status === 'incomplete';
  const vote = (helpful: boolean) => {
    if (!state.answerKey || voted !== null) return;
    setVoted(helpful);
    void sendKbFeedback({ answerKey: state.answerKey, helpful }).catch(() => {});
  };
  return (
    <section aria-live="polite" className="rounded-lg border border-border bg-panel p-4">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-sm font-medium text-fg">{t('help.answerTitle')}</h2>
        {state.cached ? <Badge tone="neutral">{t('help.cached')}</Badge> : null}
        {platformAdmin && state.status === 'done' && !state.cached && state.costUsd ? (
          <span className="text-xs tabular-nums text-muted">{usd(state.costUsd, 4)}</span>
        ) : null}
        {state.status === 'streaming' ? <span className="text-xs text-muted">{t('help.asking')}</span> : null}
      </div>
      {state.text ? (
        <div className="text-sm leading-relaxed text-fg-2">
          <SmoothMarkdown text={answer.content} live={state.status === 'streaming'} />
        </div>
      ) : null}
      {state.status === 'incomplete' || (unavailable && state.text) ? (
        <p role="status" className="mt-2 text-sm text-muted">
          {t('help.incomplete')}
        </p>
      ) : state.status === 'unavailable' && state.reason ? (
        <p className="text-sm text-muted">{t(REASON_KEY[state.reason])}</p>
      ) : null}
      {unavailable && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-hover"
        >
          {t('help.chatRetry')}
        </button>
      ) : null}
      {state.status !== 'streaming' ? (
        <HelpSources
          sources={unavailable ? state.sources : answer.sources}
          related={unavailable}
          className="mt-3"
        />
      ) : null}
      {state.status === 'done' && state.answerKey ? (
        <div className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-xs text-muted">
          {voted === null ? (
            <>
              <span>{t('help.helpful')}</span>
              <button
                type="button"
                onClick={() => vote(true)}
                className="rounded-md border border-border px-2.5 py-0.5 hover:bg-hover"
              >
                {t('help.yes')}
              </button>
              <button
                type="button"
                onClick={() => vote(false)}
                className="rounded-md border border-border px-2.5 py-0.5 hover:bg-hover"
              >
                {t('help.no')}
              </button>
            </>
          ) : (
            <span>{t('help.thanks')}</span>
          )}
        </div>
      ) : null}
    </section>
  );
}
